/**
 * typeConversion.ts
 *
 * Type conversion utilities for bridging between different type definitions
 * in the form builder system (types.ts vs form-types.ts).
 *
 * This file centralizes type conversions to maintain type safety and avoid
 * scattered `as any` casts throughout the codebase.
 */

import { Page, Field } from "../types/types";
import { FormPage, FormField } from "../types/form-types";

/**
 * Converts a FormPage (from form-types) to a Page (from types)
 * Ensures type-safe conversion between the two page representations
 *
 * @param formPage FormPage object to convert
 * @returns Converted Page object
 */
export const formPageToPage = (formPage: FormPage): Page => {
  return {
    id: formPage.id,
    title: formPage.title || "",
    order: formPage.order,
    conditions: formPage.conditions as any, // Safe cast - both use FieldCondition[]
    showIf: (formPage.showIf as "all" | "any") || "all",
  };
};

/**
 * Converts multiple FormPages to Pages
 *
 * @param formPages Array of FormPage objects
 * @returns Array of converted Page objects
 */
export const formPagesToPages = (formPages: FormPage[]): Page[] => {
  return formPages.map(formPageToPage);
};

/**
 * Converts a FormField (from form-types) to a Field (from types)
 * Note: This is a partial conversion as FormField has open properties
 *
 * @param formField FormField object to convert
 * @returns Converted Field object
 */
export const formFieldToField = (formField: FormField): Field => {
  return {
    id: formField.id,
    type: (formField.type as any) || "text", // Type mismatch between FieldType definitions
    label: formField.label || "",
    required: false,
    pageId: formField.pageId,
    sectionId: formField.sectionId,
    // FormField has open properties ([key: string]: any)
    ...(formField as any),
  };
};

/**
 * Converts multiple FormFields to Fields
 *
 * @param formFields Array of FormField objects
 * @returns Array of converted Field objects
 */
export const formFieldsToFields = (formFields: FormField[]): Field[] => {
  return formFields.map(formFieldToField);
};

/**
 * Validates that a FormPage has all required properties for condition evaluation
 *
 * @param page FormPage to validate
 * @returns true if page is valid for condition evaluation
 */
export const isValidPageForConditionEvaluation = (page: FormPage): page is Required<FormPage> & { conditions?: any[] } => {
  return !!(page && page.id && page.order !== undefined);
};

/**
 * Validates that a FormField has all required properties for condition evaluation
 *
 * @param field FormField to validate
 * @returns true if field is valid for condition evaluation
 */
export const isValidFieldForConditionEvaluation = (field: FormField): field is Required<FormField> => {
  return !!(field && field.id && field.type);
};
